import type { LucideIcon } from 'lucide-react';

/**
 * The follow-up drawer's chrome: a titled card, its label/value row, and the pill both the header
 * and the solicitation list draw a status with. Shared so the drawer and the solicitation card
 * cannot drift into two spellings of the same box.
 */

export const DRAWER_BADGE =
  'inline-flex h-[23px] w-fit shrink-0 items-center gap-1.5 rounded-full pl-2 pr-[9px] text-[12px] font-medium';

interface DrawerSectionProps {
  icon: LucideIcon;
  title: string;
  /** Right of the header strip — a stamp, a counter chip, nothing. */
  aside?: React.ReactNode;
  /** The body's own padding and layout, which the design varies from section to section. */
  bodyClassName: string;
  children: React.ReactNode;
}

export const DrawerSection = ({
  icon: Icon,
  title,
  aside,
  bodyClassName,
  children,
}: DrawerSectionProps) => (
  <section className="shrink-0 overflow-hidden rounded-xl border">
    <div className="flex items-center gap-[9px] border-b bg-muted/40 px-4 py-3">
      <Icon className="h-[15px] w-[15px] shrink-0 text-muted-foreground" aria-hidden="true" />
      <span className="text-[13px] font-semibold">{title}</span>
      {aside && <span className="ml-auto">{aside}</span>}
    </div>
    <div className={bodyClassName}>{children}</div>
  </section>
);

/** Label left, value right — and the last row keeps its border, as the mock-up draws it. */
export const DrawerRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="flex items-start justify-between gap-4 border-b py-[10px]">
    <span className="shrink-0 text-[12.5px] text-muted-foreground">{label}</span>
    <span className="min-w-0 break-words text-right text-[13px] font-medium leading-[1.45]">
      {value}
    </span>
  </div>
);
